"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { User, Users, PiggyBank, Zap, Target, ChartBar as BarChart3, ArrowRight, Sparkles, Check, X, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  title: "Direct Access to the Senior Expert",
  consultant: "You work directly with me, Yassine Essaadi. Every audit, technical strategy, and client call is handled by me personally.",
  agency: "Your account is typically assigned to junior project managers or delegated to offshore teams after the sales pitch.",
  highlight: "100% Personal Attention",
  icon: User
}, {
  title: "Cost Efficiency & Zero Overhead Markup",
  consultant: "Your entire budget goes strictly toward expert SEO strategy and execution. You do not pay for agency office overhead or account managers.",
  agency: "A significant portion of your retainer often goes toward agency overhead, account management layers and sales commissions rather than direct SEO work.",
  highlight: "Maximum ROI",
  icon: PiggyBank
}, {
  title: "Tailored Strategy, Zero Templates",
  consultant: "I craft a bespoke SEO roadmap specifically designed for your industry, target audience, and current technical setup.",
  agency: "Standardized, one-size-fits-all checklists and automated reports that treat your site like every other client.",
  highlight: "Bespoke Approach",
  icon: Target
}, {
  title: "Agility & Swift Execution",
  consultant: "Decisions and site adjustments are implemented fast without multi-layer approval chains or bureaucratic delay.",
  agency: "Slow turnarounds caused by internal agency hierarchies, ticketing queues, and corporate sign-off processes.",
  highlight: "Fast Implementation",
  icon: Zap
}, {
  title: "Honest & Transparent Reporting",
  consultant: "Direct, plain-English updates focused on qualified lead volume, organic revenue growth, and tangible search visibility.",
  agency: "Overwhelmed with vanity metrics, automated PDF exports, and superficial keyword counts that hide real impact.",
  highlight: "Revenue Focus",
  icon: BarChart3
}, {
  title: "Capped Client Roster for true Focus",
  consultant: "I strictly limit the number of active clients I partner with so I can dedicate deep focus and time to your growth.",
  agency: "Account managers are routinely forced to oversee 15 to 20+ accounts simultaneously, dividing their bandwidth.",
  highlight: "Undivided Quality",
  icon: ShieldCheck
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="py-20 md:py-28 bg-background text-foreground transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-sm font-medium bg-secondary text-secondary-foreground border border-accent/20 rounded-full">
            <Sparkles className="size-3.5 text-accent mr-1.5" />
            Why Work Directly With Me
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Independent SEO Consultant vs. Traditional Agency
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you hire me, you get a dedicated SEO strategist committed to your business growth. 
            Here is why UK and international brands choose a direct partnership with me over a traditional marketing agency.
          </p>
        </div>

        {/* Comparison Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {comparisonPoints.map((point, i) => {
          const IconComponent = point.icon;
          return <motion.div key={point.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border/80 bg-card hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-sm flex flex-col justify-between overflow-hidden">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-2.5 rounded-lg bg-secondary/80 text-primary shrink-0">
                          <IconComponent className="size-5" />
                        </div>
                        <Badge variant="outline" className="bg-accent/10 text-foreground border-accent/30 text-xs font-semibold px-2.5 py-0.5">
                          {point.highlight}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                        {point.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Consultant Approach */}
                      <div className="p-3.5 rounded-lg bg-muted/60 border border-border/60">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">
                          <Check className="size-4 text-emerald-600 shrink-0" />
                          <span>With Me (Yassine)</span>
                        </div>
                        <p className="text-sm text-foreground/90 leading-relaxed font-sans">
                          {point.consultant}
                        </p>
                      </div>

                      {/* Agency Approach */}
                      <div className="p-3.5 rounded-lg bg-background border border-border/40">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                          <X className="size-4 text-rose-500 shrink-0" />
                          <span>Traditional Agency</span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                          {point.agency}
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Direct Access Callout Banner */}
        <div className="p-8 sm:p-10 rounded-2xl bg-primary text-primary-foreground shadow-lg border border-primary/20 relative overflow-hidden mb-12">
          <div className="max-w-3xl mx-auto text-center space-y-6 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-medium uppercase tracking-wider">
              <Users className="size-3.5" />
              <span>Direct Partnership Promise</span>
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-semibold font-default tracking-tight text-primary-foreground">
              {"“You work directly with me — from audit to execution.”"}
            </h3>
            
            <p className="text-primary-foreground/90 text-base sm:text-lg leading-relaxed">
              I do not hand your project off to account executives or junior staff. When you hire YE Consultancy, 
              I personally analyze your search data, diagnose technical issues, build your content roadmap, and track your ranking results.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium text-base h-12 px-8">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 dark:hover:bg-primary-foreground/20 font-medium text-base h-12 px-8">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}